import { createContext, useContext, type ReactNode } from 'react';

/**
 * Display data the meeting-people card needs to hydrate a person row.
 * The card payload only carries stable identifiers (userId), so the host
 * resolves name + avatar however it likes (a query, a cache, …). The
 * package owns no HTTP client or endpoint knowledge.
 */
export interface BizyResolvedUser {
	displayName?: string;
	avatarUrl?: string;
	/**
	 * Optional secondary line (e.g. title · department, or @username). The host
	 * composes it from whatever profile data it has; the package stays unaware
	 * of custom-property shapes. Used by the person-picker card.
	 */
	subtitle?: string;
	isLoading: boolean;
}

export type BizyUserResolver = (userId?: string) => BizyResolvedUser;

const defaultResolver: BizyUserResolver = () => ({ isLoading: false });

const BizyUserResolverContext = createContext<BizyUserResolver>(defaultResolver);

export function BizyUserResolverProvider({ resolveUser, children }: { resolveUser?: BizyUserResolver; children: ReactNode }) {
	return <BizyUserResolverContext.Provider value={resolveUser ?? defaultResolver}>{children}</BizyUserResolverContext.Provider>;
}

export function useBizyUserResolver(): BizyUserResolver {
	return useContext(BizyUserResolverContext);
}
